import React, {useState, useEffect, useRef} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {withTranslation} from 'react-i18next';
import {Form, Field} from 'react-final-form';
import {Panel} from "primereact/panel";
import {classNames} from 'primereact/utils';
import {InputText} from 'primereact/inputtext';
import {Button} from 'primereact/button';
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import {BlockUI} from "primereact/blockui";
import {FileUpload} from "primereact/fileupload";
import {ProgressBar} from "primereact/progressbar";
import {InputMask} from "primereact/inputmask";
import {InputNumber} from "primereact/inputnumber";
import {Slider} from "primereact/slider";
import {TabPanel, TabView} from "primereact/tabview";
import {Tooltip} from "primereact/tooltip";
import download from "js-file-download";
import axios from "axios";
import Cookies from "js-cookie";
import moment from "moment";

import HelpComponent from "../HelpComponent";
import FormLocker from "../../util/FormLocker";
import {generateAccommodationCertificatePdf} from "../../util/signature";

const normalizeAdvanceInvoiceValue = (strategy, value) => {
  if (!['FIXED_AMOUNT', 'PERCENTAGE'].includes(strategy)) {
    return null;
  }
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = typeof value === 'number'
    ? value
    : Number(String(value).replace(',', '.'));

  if (!Number.isFinite(normalized)) {
    return null;
  }

  if (strategy === 'PERCENTAGE') {
    return Math.min(99, Math.max(0, Math.round(normalized)));
  }

  return Math.round(normalized);
};

const normalizeAdvanceInvoiceFields = (data) => {
  const restData = {...(data || {})};
  delete restData.ezee_centrtix_update_mode;
  const strategy = data?.advance_invoice_strategy ?? null;
  return {
    ...restData,
    advance_invoice_strategy: strategy,
    advance_invoice_value: normalizeAdvanceInvoiceValue(strategy, data?.advance_invoice_value)
  };
};

const sanitizeAccommodationPayload = (payload = {}) => {
  const sanitizedPayload = {...payload};
  delete sanitizedPayload.ezee_centrtix_update_mode;
  return sanitizedPayload;
};

const _FM_AccommodationFormComponent = (props) => {

  const {t} = props;
  const navigate = useNavigate();
  const {id, action, entity} = useParams();
  const [formData, setFormData] = useState({
    channel_manager: "NONE",
    auto_invoice: "NONE",
    advance_invoice_trigger: "NONE",
    advance_invoice_strategy: null,
    advance_invoice_value: null,
    reservation_from_to_input_type: "merged"
  });
  const [newFormData, setNewFormData] = useState({});
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [operators, setOperators] = useState([]);
  const [guestInfos, setGuestInfos] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [channelManagers, setChannelManagers] = useState([]);
  const [paymentmethods, setPaymentmethods] = useState([]);
  const [closeBehaviors, setCloseBehaviors] = useState([]);
  const [autoInvoiceBehaviors, setAutoInvoiceBehaviors] = useState([]);
  const [advanceInvoiceTriggers, setAdvanceInvoiceTriggers] = useState([]);
  const [advanceInvoiceStrategies, setAdvanceInvoiceStrategies] = useState([]);
  const [customerDataEmailSendModes, setCustomerDataEmailSendModes] = useState([]);
  const [customerDataEmailAutoRepeatSends, setCustomerDataEmailAutoRepeatSends] = useState([]);
  const [CFormData, setCFormData] = useState({});
  const [CFields, setCFields] = useState([]);
  const [COptions, setCOptions] = useState({});
  const [checkinTime, setCheckinTime] = useState('');
  const [checkoutTime, setCheckoutTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [ntakActionLoading, setNtakActionLoading] = useState(false);
  const [hotelConnectSyncLoading, setHotelConnectSyncLoading] = useState(false);
  const [hotelConnectTestCertificateLoading, setHotelConnectTestCertificateLoading] = useState(false);
  const [dailyClosureStatus, setDailyClosureStatus] = useState(null);
  const [locking, setLocking] = useState(false);
  const [apiValidationErrors, setApiValidationErrors] = useState({});
  const [submitValidationErrors, setSubmitValidationErrors] = useState({});
  const pollTimerRef = useRef(null);
  const requestHeaders = {
    'Authorization': 'Bearer ' + Cookies.get("userToken"),
    "Sys-name": Cookies.get("instanceId"),
    "Preferred-locale": Cookies.get("language")
  };

  const fieldTabMap = {
    name: 'accommodation_form',
    short_name: 'accommodation_form',
    country: 'accommodation_form',
    zip_code: 'accommodation_form',
    city: 'accommodation_form',
    street_and_number: 'accommodation_form',
    contact_name: 'accommodation_form',
    contact_email: 'accommodation_form',
    contact_phone: 'accommodation_form',
    operator_data: 'accommodation_form',
    currency_code: 'accommodation_form',
    child_age_limit: 'settings',
    default_payment_method: 'billing_tab',
    auto_check_in_time: 'settings',
    auto_check_out_time: 'settings',
    reservation_from_to_input_type: 'settings',
    daily_reservation_allowed: 'settings',
    api: 'settings',
    guest_information_request_page: 'settings',
    customer_data_email_send_mode: 'settings',
    customer_data_email_auto_repeat_send: 'settings',
    customer_data_email_reply_to: 'settings',
    auto_invoice: 'billing_tab',
    advance_invoice_trigger: 'billing_tab',
    advance_invoice_strategy: 'billing_tab',
    advance_invoice_value: 'billing_tab',
    channel_manager: 'channel_manager',
    e_zee_centrix_hotel_code: 'channel_manager',
    e_zee_centrix_auth_key: 'channel_manager',
    airbnb_calendar_url: 'channel_manager',
    szallas_calendar_url: 'channel_manager',
    booking_calendar_url: 'channel_manager'
  };

  const validate = (data) => {
    let errors = {};
    if (!data.name) {
      errors.name = t('required_field');
    }
    if (!data.short_name) {
      errors.short_name = t('required_field');
    }
    if (!data.country) {
      errors.country = t('required_field');
    }
    if (!data.currency_code) {
      errors.currency_code = t('required_field');
    }
    if (!data.zip_code) {
      errors.zip_code = t('required_field');
    }
    if (!data.operator_data) {
      errors.operator_data = t('required_field');
    }
    if (!data.city) {
      errors.city = t('required_field');
    }
    if (!data.street_and_number) {
      errors.street_and_number = t('required_field');
    }
    if (data.child_age_limit === undefined || data.child_age_limit === null || data.child_age_limit === '') {
      errors.child_age_limit = t('required_field');
    }
    if (!data.customer_data_email_send_mode) {
      errors.customer_data_email_send_mode = t('required_field');
    }
    if (data.customer_data_email_send_mode === 'AUTO' && !data.customer_data_email_auto_repeat_send) {
      errors.customer_data_email_auto_repeat_send = t('required_field');
    }
    return errors;
  };

  const onSubmit = (data, form) => {
    const createPayload = sanitizeAccommodationPayload(formData);
    const patchPayload = sanitizeAccommodationPayload(newFormData);
    setLoading(true)
    setApiValidationErrors({})
    if (formData && formData.id) {
      axios.patch(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation/" + formData.id,
        patchPayload,
        {
          params: {},
          headers: {
            'Authorization': 'Bearer ' + Cookies.get("userToken"),
            "Sys-name": Cookies.get("instanceId"),
            "Preferred-locale": Cookies.get("language")
          }
        })
        .then(response => {
          setFormData(normalizeAdvanceInvoiceFields(response.data.data));
          setNewFormData({});
          window.App.toastShow('success', t('success'), response.data.message)
          setLoading(false)
          window.App.setState({
            rerenderAccommodationList: Math.random(),
          })
          localStorage.setItem('selectedAccommodation', response.data.data.id)
          if (props.closeForm === true) {
            navigate(-1)
          }
        })
        .catch(error => {
          setLoading(false)
          setApiValidationErrors(error?.response?.data?.data?.errors || {})
          window.App.toastShow('error', t('error'), error.response.data.message, '', error.response)
        })
    } else {
      axios.post(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation",
        createPayload,
        {
          params: {},
          headers: {
            'Authorization': 'Bearer ' + Cookies.get("userToken"),
            "Sys-name": Cookies.get("instanceId"),
            "Preferred-locale": Cookies.get("language")
          }
        })
        .then(response => {
          setFormData(response.data.data)
          setLoading(false)
          window.App.toastShow('success', t('success'), response.data.message)
          window.App.setState({
            rerenderAccommodationList: Math.random(),
            selectedAccommodationId: response.data.data.id
          })
          localStorage.setItem('selectedAccommodation', response.data.data.id)
          if (props.closeForm === true) {
            navigate(-1)
          }
        })
        .catch(error => {
          setLoading(false)
          setApiValidationErrors(error?.response?.data?.data?.errors || {})
          window.App.toastShow('error', t('error'), error.response.data.message, '', error.response)
        })
    }
    //form.restart();
  };
  //const isFormFieldValid = (meta) => !!(meta.touched && meta.error);
  //const isFormFieldValid = (meta) => !!(meta.error);
  const isFormFieldValid = (meta) => {
    if (loading) return false;
    return !!(meta.error);
  };
  const getFormErrorMessage = (meta) => {
    return isFormFieldValid(meta) && <small className="p-validate">{meta.error}</small>;
  };

  const handleChildAgeLimitChange = (value) => {
    const rawValue = String(value ?? '');
    const normalizedValue = rawValue.replace(/\D+/g, '');

    if (normalizedValue !== rawValue) {
      window.App.toastShow('warn', t('warning'), t('child_age_limit_only_numbers'));
    }

    setFormData({
      ...formData,
      child_age_limit: normalizedValue
    });
    setNewFormData({
      ...newFormData,
      child_age_limit: normalizedValue
    });
  };

  FormLocker(() => {
    if (formData && formData.id && formData._class_name) {
      window.App.formLock(formData._class_name, formData.id)
    }
  }, 1000);

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: requestHeaders
    })
  }

  useEffect(() => {
    (async () => {
      try {
        const countries = await apiCall('get', 'dictionary/country');
        const allCountries = countries.data.data;
        const prioritizedCountries = JSON.parse(process.env.REACT_APP_PRIORITIZEDCOUNTRIES || "[]");
        const sortedCountries = [
          ...allCountries.filter((country) => prioritizedCountries.includes(country.code)),
          ...allCountries.filter((country) => !prioritizedCountries.includes(country.code)),
        ];
        setCountries(sortedCountries);

        const currencies = await apiCall('get', 'dictionary/currency');
        const allCurrencies = currencies.data.data;
        const prioritizedCurrencies = JSON.parse(process.env.REACT_APP_PRIORITIZEDCURRENCIES || "[]");
        const prioritizedCurrencyMap = new Map(allCurrencies.map((currency) => [currency.code, currency]));
        const sortedCurrencies = [
          ...prioritizedCurrencies
            .map((code) => prioritizedCurrencyMap.get(code))
            .filter(Boolean),
          ...allCurrencies.filter((currency) => !prioritizedCurrencies.includes(currency.code)),
        ];
        setCurrencies(sortedCurrencies);

        const operators = await apiCall('get', 'operator-data');
        setOperators(operators.data.data.items);

        const channelManagers = await apiCall('get', 'dictionary/channel-manager');
        setChannelManagers(channelManagers.data.data);

        const paymentMethods = await apiCall('get', 'payment-method');
        setPaymentmethods(paymentMethods.data.data.items);

        const autoInvoiceBehaviors = await apiCall('get', 'dictionary/auto-invoice-behavior');
        setAutoInvoiceBehaviors(autoInvoiceBehaviors.data.data);

        const advanceInvoiceTriggers = await apiCall('get', 'dictionary/advance-invoice-trigger');
        setAdvanceInvoiceTriggers(advanceInvoiceTriggers.data.data);

        const advanceInvoiceStrategies = await apiCall('get', 'dictionary/advance-invoice-strategy');
        setAdvanceInvoiceStrategies(advanceInvoiceStrategies.data.data);

        const customerDataEmailSendModes = await apiCall('get', 'dictionary/customer-data-email-send-mode');
        setCustomerDataEmailSendModes(customerDataEmailSendModes.data.data);

        const customerDataEmailAutoRepeatSends = await apiCall('get', 'dictionary/customer-data-email-auto-repeat-send');
        setCustomerDataEmailAutoRepeatSends(customerDataEmailAutoRepeatSends.data.data);

        const guestInfos = await apiCall('get', 'guest-information-request-page');
        setGuestInfos(guestInfos.data.data.items);

        const _checklistst = await apiCall('get', 'accommodation-readiness-checklist');
        setChecklists(_checklistst.data.data.items);

        if (!(window.App.urlVars().accommodationId ?? id)) {
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      }
    })();
  }, [id]);

  useEffect(() => {
    const accommodationId = window.App.urlVars().accommodationId ?? id ?? formData.id;
    if (!accommodationId) {
      return;
    }

    (async () => {
      try {
        setLoading(true);
        const accommodation = await apiCall('get', 'accommodation/' + accommodationId);
        const accommodationData = accommodation.data.data;
        setFormData({
          ...normalizeAdvanceInvoiceFields(accommodationData),
          auto_invoice: accommodationData.auto_invoice ?? accommodationData.auto_invoice_on_check_out ?? "NONE",
          advance_invoice_trigger: accommodationData.advance_invoice_trigger ?? "NONE",
          advance_invoice_strategy: accommodationData.advance_invoice_strategy ?? null,
          advance_invoice_value: normalizeAdvanceInvoiceValue(
            accommodationData.advance_invoice_strategy ?? null,
            accommodationData.advance_invoice_value
          )
        });

        if (accommodation.data.data._country_specifics && Object.keys(accommodation.data.data._country_specifics)[0].length > 0) {
          if (Object.keys(accommodation.data.data._country_specifics)[0].toLowerCase() == accommodation.data.data.country.toLowerCase()) {
            const transformedCountry = Object.keys(accommodation.data.data._country_specifics)[0]
              ? Object.keys(accommodation.data.data._country_specifics)[0].charAt(0).toUpperCase()
              + Object.keys(accommodation.data.data._country_specifics)[0].slice(1).toLowerCase() : "";
            const cfields = await apiCall('get', 'info/Accommodation' + transformedCountry);
            setCFields(cfields.data.editable_fields);

            for (let field of cfields.data.editable_fields) {
              try {
                const _options = await apiCall('get', "dictionary/" + field.name.replaceAll('_', '-'));
                setCOptions((prev) => ({
                  ...prev,
                  [field.name]: Object.values(_options.data.data).flat()
                }))
              } catch (error) {

              }
            }
          }

          let countryObjectName = "accommodation_" + Object.keys(accommodation.data.data._country_specifics)[0].toLowerCase() + "s";
          if (accommodation.data.data[countryObjectName][0] && accommodation.data.data[countryObjectName][0].id) {
            const cformdata = await apiCall('get', 'accommodation-'
              + Object.keys(accommodation.data.data._country_specifics)[0].toLowerCase() + "/" + accommodation.data.data[countryObjectName][0].id);
            setCFormData(cformdata.data.data);
          }

        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      }
    })();
  }, [id, formData.id]);

  useEffect(() => {
    if (!formData?.id) {
      setDailyClosureStatus(null);
      return;
    }
    loadDailyClosureStatus(formData.id);
  }, [formData?.id]);

  useEffect(() => {
    if (!countries[0] || formData.country !== undefined && formData.country !== null && formData.country !== '') return;
    const defaultValue = countries[0].code;
    setFormData((prev) => prev.country !== undefined && prev.country !== null && prev.country !== '' ? prev : {
      ...prev,
      country: defaultValue
    });
    if (!(window.App.urlVars().accommodationId || id || formData.id)) {
      setNewFormData((prev) => prev.country !== undefined && prev.country !== null && prev.country !== '' ? prev : {
        ...prev,
        country: defaultValue
      });
    }
  }, [countries, formData.country, formData.id, id]);

  useEffect(() => {
    if (!operators[0] || formData.operator_data !== undefined && formData.operator_data !== null && formData.operator_data !== '') return;
    const defaultValue = operators[0].id;
    setFormData((prev) => prev.operator_data !== undefined && prev.operator_data !== null && prev.operator_data !== '' ? prev : {
      ...prev,
      operator_data: defaultValue
    });
    if (!(window.App.urlVars().accommodationId || id || formData.id)) {
      setNewFormData((prev) => prev.operator_data !== undefined && prev.operator_data !== null && prev.operator_data !== '' ? prev : {
        ...prev,
        operator_data: defaultValue
      });
    }
  }, [operators, formData.operator_data, formData.id, id]);

  useEffect(() => {
    if (!currencies[0] || formData.currency_code !== undefined && formData.currency_code !== null && formData.currency_code !== '') return;
    const defaultValue = currencies[0].code;
    setFormData((prev) => prev.currency_code !== undefined && prev.currency_code !== null && prev.currency_code !== '' ? prev : {
      ...prev,
      currency_code: defaultValue
    });
    if (!(window.App.urlVars().accommodationId || id || formData.id)) {
      setNewFormData((prev) => prev.currency_code !== undefined && prev.currency_code !== null && prev.currency_code !== '' ? prev : {
        ...prev,
        currency_code: defaultValue
      });
    }
  }, [currencies, formData.currency_code, formData.id, id]);

  useEffect(() => {
    if (!guestInfos[0] || formData.guest_information_request_page !== undefined && formData.guest_information_request_page !== null && formData.guest_information_request_page !== '') return;
    const defaultValue = guestInfos[0].id;
    setFormData((prev) => prev.guest_information_request_page !== undefined && prev.guest_information_request_page !== null && prev.guest_information_request_page !== '' ? prev : {
      ...prev,
      guest_information_request_page: defaultValue
    });
    if (!(window.App.urlVars().accommodationId || id || formData.id)) {
      setNewFormData((prev) => prev.guest_information_request_page !== undefined && prev.guest_information_request_page !== null && prev.guest_information_request_page !== '' ? prev : {
        ...prev,
        guest_information_request_page: defaultValue
      });
    }
  }, [guestInfos, formData.guest_information_request_page, formData.id, id]);

  useEffect(() => {
    if (window.App.urlVars().accommodationId || id || formData.id) return;
    if (!paymentmethods[0] || formData.default_payment_method !== undefined && formData.default_payment_method !== null && formData.default_payment_method !== '') return;
    const defaultValue = paymentmethods[0].id;
    setFormData((prev) => prev.default_payment_method !== undefined && prev.default_payment_method !== null && prev.default_payment_method !== '' ? prev : {
      ...prev,
      default_payment_method: defaultValue
    });
    setNewFormData((prev) => prev.default_payment_method !== undefined && prev.default_payment_method !== null && prev.default_payment_method !== '' ? prev : {
      ...prev,
      default_payment_method: defaultValue
    });
  }, [paymentmethods, formData.default_payment_method, formData.id, id]);

  useEffect(() => {
    if (!customerDataEmailSendModes[0] || formData.customer_data_email_send_mode !== undefined && formData.customer_data_email_send_mode !== null && formData.customer_data_email_send_mode !== '') return;
    const defaultValue = customerDataEmailSendModes[0].value;
    setFormData((prev) => prev.customer_data_email_send_mode !== undefined && prev.customer_data_email_send_mode !== null && prev.customer_data_email_send_mode !== '' ? prev : {
      ...prev,
      customer_data_email_send_mode: defaultValue
    });
    if (!(window.App.urlVars().accommodationId || id || formData.id)) {
      setNewFormData((prev) => prev.customer_data_email_send_mode !== undefined && prev.customer_data_email_send_mode !== null && prev.customer_data_email_send_mode !== '' ? prev : {
        ...prev,
        customer_data_email_send_mode: defaultValue
      });
    }
  }, [customerDataEmailSendModes, formData.customer_data_email_send_mode, formData.id, id]);

  useEffect(() => {
    if (formData.customer_data_email_send_mode !== 'AUTO') return;
    if (!customerDataEmailAutoRepeatSends[0] || formData.customer_data_email_auto_repeat_send !== undefined && formData.customer_data_email_auto_repeat_send !== null && formData.customer_data_email_auto_repeat_send !== '') return;
    const defaultValue = customerDataEmailAutoRepeatSends[0].value;
    setFormData((prev) => prev.customer_data_email_auto_repeat_send !== undefined && prev.customer_data_email_auto_repeat_send !== null && prev.customer_data_email_auto_repeat_send !== '' ? prev : {
      ...prev,
      customer_data_email_auto_repeat_send: defaultValue
    });
    if (!(window.App.urlVars().accommodationId || id || formData.id)) {
      setNewFormData((prev) => prev.customer_data_email_auto_repeat_send !== undefined && prev.customer_data_email_auto_repeat_send !== null && prev.customer_data_email_auto_repeat_send !== '' ? prev : {
        ...prev,
        customer_data_email_auto_repeat_send: defaultValue
      });
    }
  }, [customerDataEmailAutoRepeatSends, formData.customer_data_email_send_mode, formData.customer_data_email_auto_repeat_send, formData.id, id]);

  const hasPersistedAccommodation = Boolean(window.App.urlVars().accommodationId || id || formData.id);
  const selectedCountryLabel = countries.find((country) => country.code === formData.country)?.name
    || (formData.country ? formData.country.toUpperCase() : 'HU');
  const countryCode = (formData?.country || '').toUpperCase();
  const isHuCountrySpecific = countryCode === 'HU';
  const ifaType = CFormData?.ifa_type;
  const normalizedIfaType = typeof ifaType === 'string' ? ifaType.toLowerCase().trim() : '';
  const isNoneLikeIfaType = !normalizedIfaType
    || normalizedIfaType === 'none'
    || /^im([1-9]|10|11)$/.test(normalizedIfaType);
  const nonEditableCountrySpecificFieldNames = new Set([
    'ntaak_private_key_path',
    'ntaak_csr_path',
    'ntaak_certificate_path',
    'ntak_private_key_path',
    'ntak_csr_path',
    'ntak_certificate_path',
    'hotel_connect_accommodation_id',
    'hotel_connect_registration_status',
    'hotel_connect_registration_error',
    'hotel_connect_csr_status',
    'hotel_connect_csr_error',
    'hotel_connect_certificate_status',
    'hotel_connect_certificate_error',
    'hotel_connect_certificate_uploaded_at',
    'hotelConnectAccommodationId',
    'hotelConnectRegistrationStatus',
    'hotelConnectRegistrationError',
    'hotelConnectCsrStatus',
    'hotelConnectCsrError',
    'hotelConnectCertificateStatus',
    'hotelConnectCertificateError',
    'hotelConnectCertificateUploadedAt'
  ]);

  const isCFieldVisible = (field) => {
    if (nonEditableCountrySpecificFieldNames.has(field?.name)) {
      return false;
    }

    const isIfaNightField = field.name === 'ifa_night_fee' || field.name === 'ifa_night';
    const isIfaPercentField = field.name === 'ifa_percent';
    if (!isHuCountrySpecific) {
      return true;
    }
    if (isNoneLikeIfaType) {
      return !(isIfaPercentField || isIfaNightField);
    }
    if (normalizedIfaType === 'percent') {
      return !isIfaNightField;
    }
    if (normalizedIfaType === 'fix_amount') {
      return !isIfaPercentField;
    }
    return true;
  };
  const visibleCFields = (CFields || []).filter((field) => isCFieldVisible(field));
  const isVisibleCFieldsFilled = visibleCFields.every((field) => {
    const value = CFormData?.[field.name];
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return value !== undefined && value !== null;
  });
  const shouldDisableNtakActions = !formData.id || !isVisibleCFieldsFilled || !CFormData.id;
  const showNtakValidationSettingsWarning = () => {
    window.App.toastShow('warn', t('warning'), t('ntak_validation_requires_saved_settings'));
  };
  const showHotelConnectCsrStatusWarning = () => {
    window.App.toastShow('warn', t('warning'), t('hotelconnect_csr_status_required_for_actions'));
  };
  const countrySpecificFieldNames = new Set((CFields || []).map((field) => field.name));
  const apiErroredFieldNames = Object.keys(apiValidationErrors || {});
  const submitErroredFieldNames = Object.keys(submitValidationErrors || {});
  const hasTabError = (fieldNames, tabKey) => {
    return fieldNames.some((fieldName) => {
      const mappedTab = fieldTabMap[fieldName];
      if (mappedTab) {
        return mappedTab === tabKey;
      }
      if (countrySpecificFieldNames.has(fieldName)) {
        return tabKey === 'country_specific_settings';
      }
      return false;
    });
  };
  const hasApiTabError = (tabKey) => {
    return hasTabError(apiErroredFieldNames, tabKey);
  };
  const hasSubmitValidationTabError = (tabKey) => {
    return hasTabError(submitErroredFieldNames, tabKey);
  };
  const getTabHeader = (translationKey) => {
    const submitHasError = hasSubmitValidationTabError(translationKey);
    return (
      <span
        className={classNames({'p-error': !submitHasError && hasApiTabError(translationKey)})}
        style={submitHasError ? {fontWeight: 700, color: '#000000'} : undefined}
      >
        {t(translationKey)}
      </span>
    );
  };

  const getFirstFilledValue = (sources, candidates) => {
    for (const source of sources) {
      if (!source) continue;
      for (const key of candidates) {
        const value = source[key];
        if (value === null || value === undefined) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        return value;
      }
    }
    return '';
  };

  const getHotelConnectStatusMeta = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
    const neutralStyle = {backgroundColor: '#dddddd', color: '#5f656b'};
    if (normalizedStatus === 'success') {
      return {badgeClass: 'p-badge-success', label: t('hotelconnect_status_success'), style: {}};
    }
    if (normalizedStatus === 'failed') {
      return {badgeClass: '', label: t('hotelconnect_status_failed'), style: {backgroundColor: '#f7a2a2', color: '#5f656b'}};
    }
    if (normalizedStatus === 'skipped') {
      return {badgeClass: 'p-badge-secondary', label: t('hotelconnect_status_skipped'), style: {}};
    }
    return {badgeClass: '', label: t('hotelconnect_status_not_started'), style: neutralStyle};
  };

  const getDailyClosureStatusMeta = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
    const neutralStyle = {backgroundColor: '#dddddd', color: '#5f656b'};
    if (normalizedStatus === 'up_to_date') {
      return {label: t('daily_closure_status_up_to_date'), style: {backgroundColor: '#0abb87', color: '#ffffff'}};
    }
    if (normalizedStatus === 'attention') {
      return {label: t('daily_closure_status_attention'), style: {backgroundColor: '#f7a2a2', color: '#5f656b'}};
    }
    if (normalizedStatus === 'pending') {
      return {label: t('daily_closure_status_pending'), style: neutralStyle};
    }
    if (normalizedStatus === 'not_ready') {
      return {label: t('daily_closure_status_not_ready'), style: neutralStyle};
    }
    if (normalizedStatus === 'not_applicable') {
      return {label: t('daily_closure_status_not_applicable'), style: neutralStyle};
    }
    return {label: t('daily_closure_status_not_started'), style: neutralStyle};
  };

  const getDailyClosureAttemptStatusMeta = (status) => {
    const normalizedStatus = typeof status === 'string' ? status.toLowerCase().trim() : '';
    const neutralStyle = {backgroundColor: '#dddddd', color: '#5f656b'};
    if (normalizedStatus === 'success') {
      return {label: t('hotelconnect_status_success'), style: {backgroundColor: '#0abb87', color: '#ffffff'}};
    }
    if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
      return {label: t('hotelconnect_status_failed'), style: {backgroundColor: '#f7a2a2', color: '#5f656b'}};
    }
    if (normalizedStatus === 'skipped') {
      return {label: t('hotelconnect_status_skipped'), style: neutralStyle};
    }
    return {label: t('daily_closure_status_not_started'), style: neutralStyle};
  };

  const formatDateTimeValue = (value) => {
    if (!value) {
      return t('hotelconnect_certificate_not_uploaded_yet');
    }
    const parsedDate = moment(value);
    if (!parsedDate.isValid()) {
      return String(value);
    }
    return parsedDate.format('YYYY-MM-DD HH:mm:ss');
  };

  const formatDailyClosureDateTimeValue = (value) => {
    if (!value) {
      return '-';
    }
    const parsedDate = moment(value);
    if (!parsedDate.isValid()) {
      return String(value);
    }
    return parsedDate.format('YYYY-MM-DD HH:mm:ss');
  };

  const getHuDataValue = (keys = []) => getFirstFilledValue([CFormData, formData], keys);
  const hotelConnectRegistrationStatus = getHuDataValue(['hotel_connect_registration_status', 'hotelConnectRegistrationStatus']);
  const hotelConnectCsrStatus = getHuDataValue(['hotel_connect_csr_status', 'hotelConnectCsrStatus']);
  const hotelConnectCertificateStatus = getHuDataValue(['hotel_connect_certificate_status', 'hotelConnectCertificateStatus']);
  const hotelConnectCertificateUploadedAt = getHuDataValue(['hotel_connect_certificate_uploaded_at', 'hotelConnectCertificateUploadedAt']);
  const hotelConnectAccommodationId = getHuDataValue(['hotel_connect_accommodation_id', 'hotelConnectAccommodationId']);
  const hasHotelConnectCertificateUploadedAt = hotelConnectCertificateUploadedAt !== null
    && hotelConnectCertificateUploadedAt !== undefined
    && String(hotelConnectCertificateUploadedAt).trim() !== '';
  const hasHotelConnectAccommodationId = hotelConnectAccommodationId !== null
    && hotelConnectAccommodationId !== undefined
    && String(hotelConnectAccommodationId).trim() !== '';
  const normalizedRegistrationStatus = String(hotelConnectRegistrationStatus || '').toLowerCase();
  const normalizedCsrStatus = String(hotelConnectCsrStatus || '').toLowerCase();
  const normalizedCertificateStatus = String(hotelConnectCertificateStatus || '').toLowerCase();
  const isCsrStatusSuccess = normalizedCsrStatus === 'success';
  const shouldShowHotelConnectSyncRetry = normalizedRegistrationStatus === 'failed';
  const hotelConnectRegistrationError = getHuDataValue(['hotel_connect_registration_error', 'hotelConnectRegistrationError']);
  const hotelConnectCsrError = getHuDataValue(['hotel_connect_csr_error', 'hotelConnectCsrError']);
  const hotelConnectCertificateError = getHuDataValue(['hotel_connect_certificate_error', 'hotelConnectCertificateError']);
  const accommodationFormSyncIdentifier = formData?.id || id || 'new';
  const hasAccommodationHu = Boolean(
    CFormData?.id
    || formData?.accommodation_hus?.[0]?.id
    || formData?.accommodation_hu?.id
    || formData?.accommodation_hu_id
  );
  const isCsrReady = normalizedRegistrationStatus === 'success' && normalizedCsrStatus === 'success';
  const hasHotelConnectFailure = [normalizedRegistrationStatus, normalizedCsrStatus, normalizedCertificateStatus].includes('failed');
  const currentUrl = (typeof window !== 'undefined' ? window.location.href : '').toLowerCase();
  const isTestCertificateHost = currentUrl.includes('test.felhomatrac') || currentUrl.includes('felhomatrac.local');
  const canGenerateHotelConnectTestCertificate = isTestCertificateHost
    && hasAccommodationHu
    && Boolean(formData?.id)
    && normalizedRegistrationStatus === 'success'
    && normalizedCsrStatus === 'success';
  const renderLabelWithErrorIcon = (rowKey, label, errorText) => (
    <div className="p-d-flex p-ai-center" style={{gap: '0.4rem'}}>
      {errorText &&
        <>
          <span
            className={`tt-hotelconnect-error-${rowKey}-form-${accommodationFormSyncIdentifier}`}
            data-pr-tooltip={errorText}
            style={{display: 'inline-flex', color: '#f44336'}}
          >
            <i className="pi pi-info-circle" style={{fontSize: '0.8rem'}}></i>
          </span>
          <Tooltip target={`.tt-hotelconnect-error-${rowKey}-form-${accommodationFormSyncIdentifier}`}/>
        </>
      }
      <span>{label}</span>
    </div>
  );

  const refreshNtakData = async () => {
    if (!formData?.id) {
      return null;
    }

    const [accommodationResponse, accommodationHuResponse] = await Promise.all([
      apiCall('get', 'accommodation/' + formData.id),
      apiCall('get', 'accommodation-hu/' + formData.id)
    ]);

    const accommodationData = accommodationResponse.data.data;
    setFormData((prev) => ({
      ...prev,
      ...normalizeAdvanceInvoiceFields(accommodationData),
      auto_invoice: accommodationData.auto_invoice ?? accommodationData.auto_invoice_on_check_out ?? "NONE",
      advance_invoice_trigger: accommodationData.advance_invoice_trigger ?? "NONE",
      advance_invoice_strategy: accommodationData.advance_invoice_strategy ?? null,
      advance_invoice_value: normalizeAdvanceInvoiceValue(
        accommodationData.advance_invoice_strategy ?? null,
        accommodationData.advance_invoice_value
      )
    }));
    const accommodationHuData = accommodationHuResponse.data.data || {};
    setCFormData(accommodationHuData);
    loadDailyClosureStatus(formData.id);
    return {
      accommodationData,
      accommodationHuData
    };
  };

  const loadDailyClosureStatus = async (accommodationId) => {
    if (!accommodationId) {
      setDailyClosureStatus(null);
      return;
    }
    try {
      const response = await apiCall('get', "accommodation/hotelconnect-ntak-napizaras-status");
      const accommodations = response?.data?.data?.accommodations || [];
      const selectedStatus = accommodations.find((item) => String(item?.accommodationId) === String(accommodationId)) || null;
      setDailyClosureStatus(selectedStatus);
    } catch (error) {
      setDailyClosureStatus(null);
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    }
  };

  const stopHotelConnectSyncPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const startHotelConnectSyncPolling = async (attempt = 0) => {
    const maxAttempts = 12;
    const pollIntervalMs = 5000;
    try {
      const loadedData = await refreshNtakData();
      const source = {
        ...(loadedData?.accommodationData || {}),
        ...(loadedData?.accommodationHuData || {})
      };
      const registrationStatus = String(getFirstFilledValue([source], ['hotel_connect_registration_status', 'hotelConnectRegistrationStatus']) || '').toLowerCase();
      const csrStatus = String(getFirstFilledValue([source], ['hotel_connect_csr_status', 'hotelConnectCsrStatus']) || '').toLowerCase();
      const certificateStatus = String(getFirstFilledValue([source], ['hotel_connect_certificate_status', 'hotelConnectCertificateStatus']) || '').toLowerCase();
      const finalStates = ['success', 'failed', 'skipped'];
      const isFinished = finalStates.includes(registrationStatus)
        && finalStates.includes(csrStatus)
        && (certificateStatus === '' || finalStates.includes(certificateStatus));

      if (isFinished || attempt >= maxAttempts) {
        stopHotelConnectSyncPolling();
        return;
      }
    } catch (error) {
      if (attempt >= maxAttempts) {
        stopHotelConnectSyncPolling();
        return;
      }
    }

    pollTimerRef.current = setTimeout(() => {
      startHotelConnectSyncPolling(attempt + 1);
    }, pollIntervalMs);
  };

  const handleHotelConnectSyncRetry = async () => {
    if (!formData?.id || !hasAccommodationHu) {
      return;
    }

    try {
      setHotelConnectSyncLoading(true);
      await axios.post(
        process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "csr/" + formData.id + "/hotelconnect-sync",
        {},
        {
          params: {},
          headers: requestHeaders
        }
      );
      window.App.toastShow('info', t('info'), t('hotelconnect_sync_started_message'));
      stopHotelConnectSyncPolling();
      startHotelConnectSyncPolling(0);
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setHotelConnectSyncLoading(false);
    }
  };

  const handleCsrDownload = async () => {
    if (!isCsrStatusSuccess) {
      showHotelConnectCsrStatusWarning();
      return;
    }

    if (shouldDisableNtakActions) {
      showNtakValidationSettingsWarning();
      return;
    }

    try {
      setNtakActionLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "csr/" + formData.id + "/download",
        {
          params: {},
          responseType: 'blob',
          headers: requestHeaders
        }
      );
      download(response.data, 'ntak_certificate.csr');
      window.App.toastShow('success', t('success'), t('download'));
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setNtakActionLoading(false);
    }
  };

  const handleCertificateUpload = async (file) => {
    if (!isCsrStatusSuccess) {
      showHotelConnectCsrStatusWarning();
      return;
    }

    if (shouldDisableNtakActions) {
      showNtakValidationSettingsWarning();
      return;
    }

    const uploadData = new FormData();
    uploadData.append('certificate', file);

    try {
      setNtakActionLoading(true);
      const response = await axios.post(
        process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "csr/" + formData.id + "/upload",
        uploadData,
        {
          params: {},
          headers: requestHeaders
        }
      );
      window.App.setState({
        sidebarAccommodationImportForm: false,
      });
      await refreshNtakData();
      window.App.toastShow('success', t('success'), response?.data?.message || t('success'));
    } catch (error) {
      window.App.customToastShow('error', t('error'), error?.response?.data?.message || error.toString());
    } finally {
      setNtakActionLoading(false);
    }
  };

  const handleHotelConnectTestCertificateDownload = async () => {
    if (!canGenerateHotelConnectTestCertificate) {
      return;
    }

    try {
      setHotelConnectTestCertificateLoading(true);
      const response = await axios.post(
        process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "csr/" + formData.id + "/hotelconnect-test-certificate",
        {},
        {
          params: {},
          responseType: 'blob',
          headers: requestHeaders
        }
      );
      download(response.data, 'hotelconnect_test_certificate.pem');
      window.App.toastShow('success', t('success'), t('download'));
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setHotelConnectTestCertificateLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const downloadCertificatePdf = async () => {
    try {
      setCertificateLoading(true);

      let operatorData = {};
      const operatorRaw = formData?.operator_data;
      const operatorId = typeof operatorRaw === 'object' ? operatorRaw?.id : operatorRaw;
      if (operatorId) {
        const operatorResponse = await axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "operator-data/" + operatorId,
          params: {},
          headers: requestHeaders
        });
        operatorData = operatorResponse?.data?.data || {};
      }

      let ntakServiceId = getFirstFilledValue(
        [formData, CFormData],
        ['ntak_adatszolgaltatasi_azon', 'ntak_service_id', 'ntak_id']
      );
      let ntakRegistrationNumber = getFirstFilledValue(
        [formData, CFormData],
        ['ntak_regisztracio_szam', 'ntak_registration_number', 'ntak_registration_id']
      );

      const accommodationId = window.App.urlVars().accommodationId ?? id ?? formData.id;
      if ((!ntakServiceId || !ntakRegistrationNumber) && accommodationId) {
        try {
          const huResponse = await axios({
            method: 'get',
            url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "accommodation-hu/" + accommodationId,
            params: {},
            headers: requestHeaders
          });
          const huData = huResponse?.data?.data || huResponse?.data || {};
          if (!ntakServiceId) {
            ntakServiceId = getFirstFilledValue([huData], ['ntak_adatszolgaltatasi_azon', 'ntak_service_id', 'ntak_id']);
          }
          if (!ntakRegistrationNumber) {
            ntakRegistrationNumber = getFirstFilledValue([huData], ['ntak_regisztracio_szam', 'ntak_registration_number', 'ntak_registration_id']);
          }
        } catch (error) {
          // A PDF ettől még elkészülhet, csak hiányzó mezőkkel.
        }
      }

      let signImageDataUrl = '';
      try {
        const signResponse = await axios({
          method: 'get',
          url: process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST + "sign-image",
          responseType: 'blob',
          params: {},
          headers: requestHeaders
        });
        signImageDataUrl = await blobToDataUrl(signResponse.data);
      } catch (error) {
        window.App.toastShow('warn', t('warning'), t('download_sign_image_failed'));
      }

      const accommodationAddress = [formData?.zip_code, formData?.city, formData?.street_and_number]
        .filter((item) => String(item || '').trim() !== '')
        .join(' ');
      const operatorSeat = [operatorData?.zip_code, operatorData?.registered_office_city, operatorData?.street_and_house_number]
        .filter((item) => String(item || '').trim() !== '')
        .join(', ');

      await generateAccommodationCertificatePdf({
        accommodationName: formData?.name,
        accommodationAddress,
        ntakServiceId,
        ntakRegistrationNumber,
        operatorCompanyName: operatorData?.company_name,
        operatorSeat,
        operatorTaxNumber: operatorData?.tax_number,
        signImageDataUrl
      });
    } catch (error) {
      window.App.toastShow('error', t('error'), error?.response?.data?.message || error.toString(), '', error?.response);
    } finally {
      setCertificateLoading(false);
    }
  };

  return (
    <>
      <BlockUI blocked={locking} className="blocked-ui-overlay" template={<div className="blocked-ui-message"><i
        className="pi pi-exclamation-triangle blocked-ui-message-icon"
        aria-hidden="true"></i>{t("module_editing_after_save_only")}</div>}>
        <Form onSubmit={onSubmit} initialValues={formData}
              validate={validate} render={({handleSubmit, form}) => (
          <form
            onSubmit={(event) => {
              const submitResult = handleSubmit(event);
              const formState = form.getState();
              if (formState.submitFailed && (formState.hasValidationErrors || formState.invalid)) {
                setSubmitValidationErrors(formState.errors || {});
                window.App.toastShow('warn', t('warning'), t('reservation_submit_required_fields_invalid'));
              } else {
                setSubmitValidationErrors({});
              }
              return submitResult;
            }}
            className=""
          >
            {loading &&
              <ProgressBar mode="indeterminate"
                           style={{height: '5px', position: 'fixed', top: 0, zIndex: 9}}></ProgressBar>
            }
            <div className={"p-grid p-mt-3"}>
              <div className={"p-col p-col-align-center"}>
                <h2 className={"p-m-0"}>{t('accommodation_form')}</h2>
              </div>
              <div className={"p-col p-text-right p-col-align-center"}>
                <HelpComponent type={"site"} position={"UNDER_HELP_BUTTON"}/>
                <Button type="button"
                        aria-label={t('certificate_download')}
                        label={t('certificate_download')}
                        icon={"pi pi-download"}
                        loading={certificateLoading}
                        disabled={!hasPersistedAccommodation}
                        onClick={downloadCertificatePdf}
                        className="p-button-secondary p-ml-2"/>
                <Button type="submit" aria-label={t('save')} icon={"pi pi-save"}
                        loading={loading}
                        onClick={() => {
                          props = {...props, closeForm: false}
                        }}
                        className="p-button-success p-ml-2"/>
                {window.location.href.includes('#/accommodation') && formData.id &&
                  <Button type="submit" aria-label={t('save_and_close')}
                          loading={loading}
                          onClick={() => {
                            props = {...props, closeForm: true}
                          }}
                          className="p-button-success p-ml-2">
                    <span>
                      <i className={"pi pi-save"}></i>&nbsp;&nbsp;<i className={"pi pi-backward"}></i>
                    </span>
                  </Button>
                }
                <Button type="button"
                        icon={"pi pi-times"}
                        onClick={() => {
                          navigate(-1)
                        }}
                        className="p-button-secondary close p-ml-2">
                </Button>
              </div>
            </div>
            {/*<HelpComponent type={"site"} position={"IN_ACTIVE_BOX"}/>*/}
            <HelpComponent type={"edit_box"}/>
            <TabView className={"p-mt-3"} renderActiveOnly={false}>
              <TabPanel header={getTabHeader('accommodation_form')}>
                <HelpComponent type={"tab"} tabNumber={1}/>
                <Panel className={"p-mt-0"}>
                  <h3 className={"p-mt-0"}>{t('accommodation_identification')}</h3>
                  <HelpComponent type={"section"} tabNumber={1} sectionNumber={1}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-6 p-xl-6"}>
                      <Field name="name" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"name"}/>
                          <label htmlFor="name"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('name')}</label>
                          <span className="p-d-block">
                        <InputText id="name" {...input}
                                   onChange={(e) => {
                                     setFormData({
                                       ...formData,
                                       name: e.target.value
                                     })
                                     setNewFormData({
                                       ...newFormData,
                                       name: e.target.value
                                     })
                                   }}
                                   value={formData.name}
                                   className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-3"}>
                      <Field name="short_name" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"short_name"}/>
                          <label htmlFor="short_name"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('short_name')}</label>
                          <span className="p-d-block">
                        <InputText id="short_name" {...input}
                                   onChange={(e) => {
                                     setFormData({
                                       ...formData,
                                       short_name: e.target.value
                                     })
                                     setNewFormData({
                                       ...newFormData,
                                       short_name: e.target.value
                                     })
                                   }}
                                   value={formData.short_name}
                                   className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-3"}>
                      <Field name="country" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"country"}/>
                          <label htmlFor="country"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('country')}</label>
                          <span className="p-d-block">
                        <Dropdown options={countries}
                                  filter
                                  disabled={formData.id}
                                  optionLabel="name"
                                  optionValue="code"
                                  id={"country"}
                                  emptyMessage={t('empty_message')}
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      country: e.value
                                    })
                                    setNewFormData({
                                      ...newFormData,
                                      country: e.value
                                    })
                                  }}
                                  value={formData.country}
                        />
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  </div>
                </Panel>
                <Panel className={"p-mt-3"}>
                  <h3 className={"p-mt-0"}>{t('accommodation_location')}</h3>
                  <HelpComponent type={"section"} tabNumber={1} sectionNumber={2}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-3"}>
                      <Field name="zip_code" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"zip_code"}/>
                          <label htmlFor="zip_code"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('zip_code')}</label>
                          <span className="p-d-block">
                      <InputText id="zip_code" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     zip_code: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     zip_code: e.target.value
                                   })
                                   if (e.target.value.length >= 4) {
                                     window.App.zipCode(e.target.value, (city) => {
                                       setFormData(prev => ({
                                         ...prev,
                                         city: city
                                       }));
                                     });
                                   }
                                 }}
                                 value={formData.zip_code}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-3"}>
                      <Field name="city" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"city"}/>
                          <label htmlFor="city"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('city')}</label>
                          <span className="p-d-block">
                      <InputText id="city" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     city: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     city: e.target.value
                                   })
                                 }}
                                 value={formData.city}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-6 p-xl-6"}>
                      <Field name="street_and_number" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"street_and_number"}/>
                          <label htmlFor="street_and_number"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('street_and_number')}</label>
                          <span className="p-d-block">
                      <InputText id="street_and_number" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     street_and_number: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     street_and_number: e.target.value
                                   })
                                 }}
                                 value={formData.street_and_number}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  </div>
                </Panel>
                <Panel className={"p-mt-3"}>
                  <h3 className={"p-mt-0"}>{t('accommodation_contact')}</h3>
                  <HelpComponent type={"section"} tabNumber={1} sectionNumber={3}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="contact_name" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"contact_name"}/>
                          <label htmlFor="contact_name"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('contact_name')}</label>
                          <span className="p-d-block">
                      <InputText id="contact_name" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     contact_name: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     contact_name: e.target.value
                                   })
                                 }}
                                 value={formData.contact_name}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="contact_email" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"contact_email"}/>
                          <label htmlFor="contact_email"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('contact_email')}</label>
                          <span className="p-d-block">
                      <InputText id="contact_email" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     contact_email: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     contact_email: e.target.value
                                   })
                                 }}
                                 value={formData.contact_email}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="contact_phone" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"contact_phone"}/>
                          <label htmlFor="contact_phone"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('contact_phone')}</label>
                          <span className="p-d-block">
                      <InputText id="contact_phone" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     contact_phone: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     contact_phone: e.target.value
                                   })
                                 }}
                                 value={formData.contact_phone}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  </div>
                </Panel>
                <Panel className={"p-mt-3"}>
                  <h3 className={"p-mt-0"}>{t('accommodation_operation')}</h3>
                  <HelpComponent type={"section"} tabNumber={1} sectionNumber={4}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="operator_data" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"operator_data"}/>
                          <label htmlFor="operator_data"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('operator_data')}</label>
                          <span className="p-d-block">
                      <Dropdown options={operators}
                                optionLabel="company_name"
                                optionValue="id"
                                id={"operator_data"}
                                emptyMessage={t('empty_message')}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    operator_data: e.value
                                  })
                                  setNewFormData({
                                    ...newFormData,
                                    operator_data: e.value
                                  })
                                }}
                                value={formData?.operator_data?.id || formData.operator_data}
                      />
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-3"}>
                      <Field name="currency_code" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"currency_code"}/>
                          <label htmlFor="currency_code"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('currency')}</label>
                          <span className="p-d-block">
                      <Dropdown options={currencies}
                                filter
                                disabled={formData.id}
                                optionLabel="code"
                                optionValue="code"
                                id={"currency_code"}
                                emptyMessage={t('empty_message')}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    currency_code: e.value
                                  })
                                  setNewFormData({
                                    ...newFormData,
                                    currency_code: e.value
                                  })
                                }}
                                value={formData.currency_code}
                      />
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  </div>
                </Panel>
              </TabPanel>
              <TabPanel header={getTabHeader('settings')}>
                <HelpComponent type={"tab"} tabNumber={2}/>
                <Panel>
                  <h3 className={"p-mt-0"}>{t('settings')}</h3>
                  <HelpComponent type={"section"} tabNumber={2} sectionNumber={1}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="child_age_limit" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"child_age_limit"}/>
                          <label htmlFor="child_age_limit"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('child_age_limit')}</label>
                          <span className="p-d-block">
                        <InputText id="child_age_limit" {...input}
                                   onChange={(e) => {
                                     handleChildAgeLimitChange(e.target.value);
                                   }}
                                   value={formData.child_age_limit}
                                   className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="auto_check_in_time" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"auto_check_in_time"}/>
                          <label htmlFor="auto_check_in_time"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('auto_check_in_time')}</label>
                          <span className="p-d-block">
                          <InputMask id={"auto_check_in_time"}
                                     mask={'99:99'}
                                     value={formData.auto_check_in_time}
                                     onChange={(e) => {
                                       if (e.value.indexOf("_") === -1) {
                                         if (e.value === '') {
                                           e.value = null
                                         }
                                         setFormData({
                                           ...formData,
                                           auto_check_in_time: e.value
                                         })
                                         setNewFormData({
                                           ...newFormData,
                                           auto_check_in_time: e.value
                                         })
                                       }
                                     }}
                                     className={classNames({'p-invalid': isFormFieldValid(meta)})}
                          />
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="auto_check_out_time" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"auto_check_out_time"}/>
                          <label htmlFor="auto_check_out_time"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('auto_check_out_time')}</label>
                          <span className="p-d-block">
                        <InputMask id={"auto_check_out_time"}
                                   mask={'99:99'}
                                   value={formData.auto_check_out_time}
                                   onChange={(e) => {
                                     if (e.value.indexOf("_") === -1) {
                                       if (e.value === '') {
                                         e.value = null
                                       }
                                       setFormData({
                                         ...formData,
                                         auto_check_out_time: e.value
                                       })
                                       setNewFormData({
                                         ...newFormData,
                                         auto_check_out_time: e.value
                                       })
                                     }
                                   }}
                                   className={classNames({'p-invalid': isFormFieldValid(meta)})}
                        />
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="reservation_from_to_input_type" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <label htmlFor="reservation_from_to_input_type"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('reservation_from_to_input_type')}</label>
                          <span className="p-d-block">
                        <Dropdown
                          options={[
                            {label: t('reservation_from_to_input_type_merged'), value: 'merged'},
                            {label: t('reservation_from_to_input_type_separated'), value: 'separated'}
                          ]}
                          optionLabel="label"
                          optionValue="value"
                          id={"reservation_from_to_input_type"}
                          emptyMessage={t('empty_message')}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              reservation_from_to_input_type: e.value
                            })
                            setNewFormData({
                              ...newFormData,
                              reservation_from_to_input_type: e.value
                            })
                          }}
                          value={formData.reservation_from_to_input_type ?? 'merged'}
                        />
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="daily_reservation_allowed" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"daily_reservation_allowed"}/>
                          <label htmlFor="daily_reservation_allowed"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('daily_reservation_allowed')}</label>
                          <span className="p-d-block">
                      <InputSwitch
                        className={"p-mt-1"}
                        name={"daily_reservation_allowed"}
                        value={formData.daily_reservation_allowed}
                        checked={formData.daily_reservation_allowed}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            daily_reservation_allowed: e.value
                          })
                          setNewFormData({
                            ...newFormData,
                            daily_reservation_allowed: e.value
                          })
                        }}/>
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    {/*<div className={"p-col-12 p-lg-4"}>*/}
                    {/*  <Field name="guest_data_from_guest_request" render={({input, meta}) => (*/}
                    {/*    <div className="p-field p-fluid">*/}
                    {/*      <HelpComponent type={"field"} field={"guest_data_from_guest_request"}/>*/}
                    {/*      <label htmlFor="guest_data_from_guest_request"*/}
                    {/*             className={classNames({'p-error': isFormFieldValid(meta)})}>{t('guest_data_from_guest_request')}</label>*/}
                    {/*      <span className="p-d-block">*/}
                    {/*    <InputSwitch*/}
                    {/*      className={"p-mt-1"}*/}
                    {/*      name={"guest_data_from_guest_request"}*/}
                    {/*      checked={formData.guest_data_from_guest_request}*/}
                    {/*      onChange={(e) => {*/}
                    {/*        setFormData({*/}
                    {/*          ...formData,*/}
                    {/*          guest_data_from_guest_request: e.value*/}
                    {/*        })*/}
                    {/*        setNewFormData({*/}
                    {/*          ...newFormData,*/}
                    {/*          guest_data_from_guest_request: e.value*/}
                    {/*        })*/}
                    {/*      }}/>*/}
                    {/*  </span>*/}
                    {/*      {getFormErrorMessage(meta)}*/}
                    {/*    </div>*/}
                    {/*  )}/>*/}
                    {/*</div>*/}
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="api" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"api"}/>
                          <label htmlFor="api"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('api')}</label>
                          <span className="p-d-block">
                        <InputSwitch
                          className={"p-mt-1"}
                          name={"api"}
                          checked={formData.api}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              api: e.value
                            })
                            setNewFormData({
                              ...newFormData,
                              api: e.value
                            })
                          }}/>
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  </div>
                </Panel>
                <Panel className={"p-mt-3"}>
                  <h3 className={"p-mt-0"}>{t('booking_data_request_tab')}</h3>
                  <HelpComponent type={"section"} tabNumber={2} sectionNumber={2}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-6"}>
                      <Field name="guest_information_request_page" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"guest_information_request_page"}/>
                          <label htmlFor="guest_information_request_page"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('guest_information_request_page')}</label>
                          <span className="p-inputgroup">
                          <Dropdown options={guestInfos}
                                    optionLabel="name"
                                    optionValue="id"
                                    id={"guest_information_request_page"}
                                    emptyMessage={t('empty_message')}
                                    onChange={(e) => {
                                      setFormData({
                                        ...formData,
                                        guest_information_request_page: e.value
                                      })
                                      setNewFormData({
                                        ...newFormData,
                                        guest_information_request_page: e.value
                                      })
                                    }}
                                    value={formData?.guest_information_request_page?.id || formData?.guest_information_request_page}
                          />
                        </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-6"}>
                      <Field name="customer_data_email_send_mode" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"customer_data_email_send_mode"}/>
                          <label htmlFor="customer_data_email_send_mode"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('customer_data_email_send_mode')}</label>
                          <span className="p-d-block">
                        <Dropdown options={customerDataEmailSendModes}
                                  optionLabel={(option) => window.App.getTranslatedLabel(option.key)}
                                  optionValue="value"
                                  id={"customer_data_email_send_mode"}
                                  emptyMessage={t('empty_message')}
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      customer_data_email_send_mode: e.value
                                    })
                                    setNewFormData({
                                      ...newFormData,
                                      customer_data_email_send_mode: e.value
                                    })
                                  }}
                                  value={formData.customer_data_email_send_mode}
                        />
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    {formData.customer_data_email_send_mode === 'AUTO' &&
                      <>
                        <div className={"p-col-12 p-lg-6"}>
                          <Field name="customer_data_email_auto_repeat_send" render={({input, meta}) => (
                            <div className="p-field p-fluid">
                              <HelpComponent type={"field"} field={"customer_data_email_auto_repeat_send"}/>
                              <label htmlFor="customer_data_email_auto_repeat_send"
                                     className={classNames({'p-error': isFormFieldValid(meta)})}>{t('customer_data_email_auto_repeat_send')}</label>
                              <span className="p-d-block">
                        <Dropdown options={customerDataEmailAutoRepeatSends}
                                  optionLabel={(option) => window.App.getTranslatedLabel(option.key)}
                                  optionValue="value"
                                  id={"customer_data_email_auto_repeat_send"}
                                  emptyMessage={t('empty_message')}
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      customer_data_email_auto_repeat_send: e.value
                                    })
                                    setNewFormData({
                                      ...newFormData,
                                      customer_data_email_auto_repeat_send: e.value
                                    })
                                  }}
                                  value={formData.customer_data_email_auto_repeat_send}
                                  disabled={formData.customer_data_email_send_mode === 'MANUAL'}
                        />
                    </span>
                              {getFormErrorMessage(meta)}
                            </div>
                          )}/>
                        </div>
                        <div className={"p-col-12 p-lg-6"}>
                          <Field name="customer_data_email_reply_to" render={({input, meta}) => (
                            <div className="p-field p-fluid">
                              <HelpComponent type={"field"} field={"customer_data_email_reply_to"}/>
                              <label htmlFor="customer_data_email_reply_to"
                                     className={classNames({'p-error': isFormFieldValid(meta)})}>{t('customer_data_email_reply_to')}</label>
                              <span className="p-d-block">
                        <InputText id="customer_data_email_reply_to" type="email" {...input}
                                   onChange={(e) => {
                                     setFormData({
                                       ...formData,
                                       customer_data_email_reply_to: e.target.value
                                     })
                                     setNewFormData({
                                       ...newFormData,
                                       customer_data_email_reply_to: e.target.value
                                     })
                                   }}
                                   value={formData.customer_data_email_reply_to}
                                   className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                              {getFormErrorMessage(meta)}
                            </div>
                          )}/>
                        </div>
                      </>
                    }
                  </div>
                </Panel>
              </TabPanel>
              <TabPanel header={getTabHeader('billing_tab')}>
                <HelpComponent type={"tab"} tabNumber={3}/>
                <Panel>
                  <h3 className={"p-mt-0"}>{t('billing_tab')}</h3>
                  <HelpComponent type={"section"} tabNumber={3} sectionNumber={1}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="default_payment_method" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"default_payment_method"}/>
                          <label htmlFor="default_payment_method"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('default_payment_method')}</label>
                          <span className="p-d-block">
                          <Dropdown options={paymentmethods}
                                    filter
                                    optionLabel={(option) => window.App.getTranslatedLabel(option.name)?.replaceAll('_', ' ')}
                                    optionValue="id"
                                    id={"default_payment_method"}
                                    emptyMessage={t('empty_message')}
                                    onChange={(e) => {
                                      setFormData({
                                        ...formData,
                                        default_payment_method: e.value
                                      })
                                      setNewFormData({
                                        ...newFormData,
                                        default_payment_method: e.value
                                      })
                                    }}
                                    value={formData?.default_payment_method?.id || formData?.default_payment_method}
                          />
                        </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="auto_invoice" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"auto_invoice"}/>
                          <label htmlFor="auto_invoice"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('auto_invoice_on_check_out')}</label>
                          <span className="p-d-block">
                        <Dropdown options={autoInvoiceBehaviors}
                                  optionLabel={(option) => window.App.getTranslatedLabel(option.key)}
                                  optionValue="value"
                                  id={"auto_invoice"}
                                  emptyMessage={t('empty_message')}
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      auto_invoice: e.value
                                    })
                                    setNewFormData({
                                      ...newFormData,
                                      auto_invoice: e.value
                                    })
                                  }}
                                  value={formData.auto_invoice}
                        />
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="advance_invoice_trigger" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"advance_invoice_trigger"}/>
                          <label htmlFor="advance_invoice_trigger"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('advance_invoice_trigger')}</label>
                          <span className="p-d-block">
                        <Dropdown options={advanceInvoiceTriggers}
                                  optionLabel={(option) => window.App.getTranslatedLabel(option.key)}
                                  optionValue="value"
                                  id={"advance_invoice_trigger"}
                                  emptyMessage={t('empty_message')}
                                  onChange={(e) => {
                                    const nextTrigger = e.value;
                                    const shouldDisableAdvanceSettings = nextTrigger === 'NONE';
                                    const nextStrategy = shouldDisableAdvanceSettings ? null : formData.advance_invoice_strategy;
                                    const nextValue = shouldDisableAdvanceSettings ? null : formData.advance_invoice_value;
                                    setFormData({
                                      ...formData,
                                      advance_invoice_trigger: nextTrigger,
                                      advance_invoice_strategy: nextStrategy,
                                      advance_invoice_value: nextValue
                                    })
                                    setNewFormData({
                                      ...newFormData,
                                      advance_invoice_trigger: nextTrigger,
                                      advance_invoice_strategy: nextStrategy,
                                      advance_invoice_value: nextValue
                                    })
                                  }}
                                  value={formData.advance_invoice_trigger}
                        />
                    </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    <div className={"p-col-12 p-lg-4"}>
                      <Field name="advance_invoice_strategy" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"advance_invoice_strategy"}/>
                          <label htmlFor="advance_invoice_strategy"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('advance_invoice_strategy')}</label>
                          <span className="p-d-block">
                        <Dropdown options={advanceInvoiceStrategies}
                                  optionLabel={(option) => window.App.getTranslatedLabel(option.key)}
                                  optionValue="value"
                                  id={"advance_invoice_strategy"}
                                  emptyMessage={t('empty_message')}
                                  disabled={formData.advance_invoice_trigger === 'NONE'}
                                  onChange={(e) => {
                                    const nextStrategy = e.value;
                                    const strategyTypes = ['FIXED_AMOUNT', 'PERCENTAGE'];
                                    const shouldResetValue = !strategyTypes.includes(nextStrategy);
                                    const isSwitchingBetweenValueStrategies = strategyTypes.includes(formData.advance_invoice_strategy)
                                      && strategyTypes.includes(nextStrategy)
                                      && formData.advance_invoice_strategy !== nextStrategy;
                                    const isAdvanceValueEmpty = formData.advance_invoice_value === null
                                      || formData.advance_invoice_value === undefined
                                      || formData.advance_invoice_value === '';
                                    let nextValue = formData.advance_invoice_value;
                                    if (shouldResetValue) {
                                      nextValue = null;
                                    } else if (isSwitchingBetweenValueStrategies) {
                                      nextValue = nextStrategy === 'FIXED_AMOUNT' ? 0 : null;
                                    } else if (nextStrategy === 'FIXED_AMOUNT' && isAdvanceValueEmpty) {
                                      nextValue = 0;
                                    } else if (nextStrategy === 'PERCENTAGE' && isAdvanceValueEmpty) {
                                      nextValue = 10;
                                    }
                                    setFormData({
                                      ...formData,
                                      advance_invoice_strategy: nextStrategy,
                                      advance_invoice_value: nextValue
                                    })
                                    setNewFormData({
                                      ...newFormData,
                                      advance_invoice_strategy: nextStrategy,
                                      advance_invoice_value: nextValue
                                    })
                                  }}
                                  value={formData.advance_invoice_strategy}
                        />
                    </span>
                          {formData.advance_invoice_trigger === 'NONE' && (
                            <small className="p-text-secondary">{t('advance_invoice_disabled_helper')}</small>
                          )}
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                    {formData.advance_invoice_trigger !== 'NONE' && ['FIXED_AMOUNT', 'PERCENTAGE'].includes(formData.advance_invoice_strategy) && (
                      <div className={"p-col-12 p-lg-4"}>
                        <Field name="advance_invoice_value" render={({input, meta}) => (
                          <div className="p-field p-fluid">
                            <HelpComponent type={"field"} field={"advance_invoice_value"}/>
                            <label htmlFor="advance_invoice_value"
                                   className={classNames({'p-error': isFormFieldValid(meta)})}>{t('advance_invoice_value')}</label>
                            <span className="p-d-block">
                            {formData.advance_invoice_strategy === 'FIXED_AMOUNT' && (
                              <InputNumber id="advance_invoice_value"
                                           value={formData.advance_invoice_value}
                                           mode="currency"
                                           locale="hu-HU"
                                           currency={formData.currency || formData.currency_code}
                                           currencyDisplay="code"
                                           minFractionDigits={0}
                                           maxFractionDigits={0}
                                           onValueChange={(e) => {
                                             setFormData({
                                               ...formData,
                                               advance_invoice_value: e.value
                                             })
                                             setNewFormData({
                                               ...newFormData,
                                               advance_invoice_value: e.value
                                             })
                                           }}
                              />
                            )}
                              {formData.advance_invoice_strategy === 'PERCENTAGE' && (
                                <div className="p-d-flex p-ai-center">
                                  <Slider id="advance_invoice_value"
                                          value={formData.advance_invoice_value ?? 10}
                                          min={0}
                                          max={99}
                                          step={1}
                                          onChange={(e) => {
                                            setFormData({
                                              ...formData,
                                              advance_invoice_value: e.value
                                            })
                                            setNewFormData({
                                              ...newFormData,
                                              advance_invoice_value: e.value
                                            })
                                          }}
                                          style={{width: '100%'}}
                                  />
                                  <span style={{marginLeft: '0.75rem', minWidth: '3rem', textAlign: 'right'}}>
                                  {Number(formData.advance_invoice_value) ?? 10}%
                                </span>
                                </div>
                              )}
                          </span>
                            {getFormErrorMessage(meta)}
                          </div>
                        )}/>
                      </div>
                    )}
                  </div>
                </Panel>
              </TabPanel>
              <TabPanel header={getTabHeader('channel_manager')}>
                <HelpComponent type={"tab"} tabNumber={4}/>
                <Panel>
                  <h3 className={"p-mt-0"}>{t('channel_manager')}</h3>
                  <HelpComponent type={"section"} tabNumber={4} sectionNumber={1}/>
                  <div className={"p-grid"}>
                    <div className={"p-col-12 p-lg-6"}>
                      <Field name="channel_manager" render={({input, meta}) => (
                        <div className="p-field p-fluid">
                          <HelpComponent type={"field"} field={"channel_manager"}/>
                          <label htmlFor="channel_manager"
                                 className={classNames({'p-error': isFormFieldValid(meta)})}>{t('channel_manager')}</label>
                          <span className="p-d-block">
                        <Dropdown options={channelManagers}
                                  filter
                                  optionLabel={(option) => window.App.getTranslatedLabel(option.key)}
                                  optionValue="value"
                                  id={"channel_manager"}
                                  emptyMessage={t('empty_message')}
                                  onChange={(e) => {
                                    setFormData({
                                      ...formData,
                                      channel_manager: e.value
                                    })
                                    setNewFormData({
                                      ...newFormData,
                                      channel_manager: e.value
                                    })
                                  }}
                                  value={formData.channel_manager}
                        />
                      </span>
                          {getFormErrorMessage(meta)}
                        </div>
                      )}/>
                    </div>
                  </div>
                  {formData.channel_manager === 'EZEE' &&
                    <div className={"p-grid"}>
                      <div className={"p-col-12 p-lg-12 p-xl-12"}>
                        <Field name="e_zee_centrix_hotel_code" render={({input, meta}) => (
                          <div className="p-field p-fluid">
                            <HelpComponent type={"field"} field={"e_zee_centrix_hotel_code"}/>
                            <label htmlFor="e_zee_centrix_hotel_code"
                                   className={classNames({'p-error': isFormFieldValid(meta)})}>{t('ezee_centrix_hotel_code')}</label>
                            <span className="p-d-block">
                      <InputText id="e_zee_centrix_hotel_code" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     e_zee_centrix_hotel_code: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     e_zee_centrix_hotel_code: e.target.value
                                   })
                                 }}
                                 value={formData.e_zee_centrix_hotel_code}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                            {getFormErrorMessage(meta)}
                          </div>
                        )}/>
                      </div>
                      <div className={"p-col-12 p-lg-12 p-xl-12"}>
                        <Field name="e_zee_centrix_auth_key" render={({input, meta}) => (
                          <div className="p-field p-fluid">
                            <HelpComponent type={"field"} field={"e_zee_centrix_auth_key"}/>
                            <label htmlFor="e_zee_centrix_auth_key"
                                   className={classNames({'p-error': isFormFieldValid(meta)})}>{t('ezee_centrix_auth_key')}</label>
                            <span className="p-d-block">
                      <InputText id="e_zee_centrix_auth_key" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     e_zee_centrix_auth_key: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     e_zee_centrix_auth_key: e.target.value
                                   })
                                 }}
                                 value={formData.e_zee_centrix_auth_key}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                            {getFormErrorMessage(meta)}
                          </div>
                        )}/>
                      </div>
                    </div>
                  }
                  {formData.channel_manager === 'FELHOMATRAC_ICAL_FEED' &&
                    <div className={"p-grid"}>
                      <div className={"p-col-12 p-lg-12 p-xl-12"}>
                        <Field name="airbnb_calendar_url" render={({input, meta}) => (
                          <div className="p-field p-fluid">
                            <HelpComponent type={"field"} field={"airbnb_calendar_url"}/>
                            <label htmlFor="airbnb_calendar_url"
                                   className={classNames({'p-error': isFormFieldValid(meta)})}>{t('airbnb_calendar_url')}</label>
                            <span className="p-d-block">
                      <InputText id="airbnb_calendar_url" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     airbnb_calendar_url: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     airbnb_calendar_url: e.target.value
                                   })
                                 }}
                                 value={formData.airbnb_calendar_url}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                    </span>
                            {getFormErrorMessage(meta)}
                          </div>
                        )}/>
                      </div>
                      <div className={"p-col-12 p-lg-12 p-xl-12"}>
                        <Field name="szallas_calendar_url" render={({input, meta}) => (
                          <div className="p-field p-fluid">
                            <HelpComponent type={"field"} field={"szallas_calendar_url"}/>
                            <label htmlFor="szallas_calendar_url"
                                   className={classNames({'p-error': isFormFieldValid(meta)})}>{t('szallas_calendar_url')}</label>
                            <span className="p-d-block">
                      <InputText id="szallas_calendar_url" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     szallas_calendar_url: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     szallas_calendar_url: e.target.value
                                   })
                                 }}
                                 value={formData.szallas_calendar_url}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      </span>
                            {getFormErrorMessage(meta)}
                          </div>
                        )}/>
                      </div>
                      <div className={"p-col-12 p-lg-12 p-xl-12"}>
                        <Field name="booking_calendar_url" render={({input, meta}) => (
                          <div className="p-field p-fluid">
                            <HelpComponent type={"field"} field={"booking_calendar_url"}/>
                            <label htmlFor="booking_calendar_url"
                                   className={classNames({'p-error': isFormFieldValid(meta)})}>{t('booking_calendar_url')}</label>
                            <span className="p-d-block">
                      <InputText id="booking_calendar_url" {...input}
                                 onChange={(e) => {
                                   setFormData({
                                     ...formData,
                                     booking_calendar_url: e.target.value
                                   })
                                   setNewFormData({
                                     ...newFormData,
                                     booking_calendar_url: e.target.value
                                   })
                                 }}
                                 value={formData.booking_calendar_url}
                                 className={classNames({'p-invalid': isFormFieldValid(meta)})}/>
                      </span>
                            {getFormErrorMessage(meta)}
                          </div>
                        )}/>
                      </div>
                    </div>
                  }
                </Panel>
              </TabPanel>
              <TabPanel header={getTabHeader('country_specific_settings')}>
                <HelpComponent type={"tab"} tabNumber={5}/>
                <Panel>
                  <h3 className={"p-mt-0"}>
                    {selectedCountryLabel}
                  </h3>
                  <HelpComponent type={"section"} tabNumber={5} sectionNumber={1}/>
                  {!hasPersistedAccommodation &&
                    <p className={"p-my-2"}>{t('country_specific_settings_after_save')}</p>
                  }
                  {hasPersistedAccommodation && formData.id && CFields && CFields[0] &&
                    <>
                      <div className={"p-grid"}>
                        {CFields && CFields[0] &&
                          <>
                            {CFields.map((field, index) => {
                              if (!isCFieldVisible(field)) {
                                return null;
                              }

                              if (field.type.includes("Text")) {
                                return (
                                  <React.Fragment key={field.name || index}>
                                    <div className={"p-col-12 p-lg-6"}>
                                      <div className="p-field p-fluid">
                                        <label htmlFor={field.name}>{t(field.name)}</label>
                                        <span className="p-d-block">
                                      <InputText id={field.name}
                                                 disabled={!formData.id}
                                                 onChange={(e) => {
                                                   setCFormData({
                                                     ...CFormData,
                                                     accommodation: formData.id,
                                                     [field.name]: e.target.value
                                                   })
                                                 }}
                                                 value={CFormData[field.name]}
                                      />
                                    </span>
                                      </div>
                                    </div>
                                  </React.Fragment>
                                )
                              }
                              if (field.type.includes("Integer")) {
                                const isHuPercentField = isHuCountrySpecific
                                  && normalizedIfaType === 'percent'
                                  && field.name === 'ifa_percent';
                                const percentValueRaw = CFormData[field.name];
                                const percentValue = percentValueRaw === '' || percentValueRaw === null || percentValueRaw === undefined
                                  ? 4
                                  : Number(percentValueRaw);
                                return (
                                  <React.Fragment key={field.name || index}>
                                    <div className={"p-col-12 p-lg-6"}>
                                      <div className="p-field p-fluid">
                                        <label htmlFor={field.name}>{t(field.name)}</label>
                                        <span className="p-d-block">
                                          {isHuPercentField ? (
                                            <div className="p-d-flex p-ai-center">
                                              <Slider id={field.name}
                                                      value={Number.isFinite(percentValue) ? percentValue : 4}
                                                      min={0}
                                                      max={99}
                                                      step={1}
                                                      disabled={!formData.id}
                                                      onChange={(e) => {
                                                        setCFormData({
                                                          ...CFormData,
                                                          accommodation: formData.id,
                                                          [field.name]: e.value
                                                        })
                                                      }}
                                                      style={{width: '100%'}}
                                              />
                                              <span style={{marginLeft: '0.75rem', minWidth: '3rem', textAlign: 'right'}}>
                                                {Number.isFinite(percentValue) ? percentValue : 4}%
                                              </span>
                                            </div>
                                          ) : (
                                            <InputText id={field.name}
                                                       keyfilter={"num"}
                                                       disabled={!formData.id}
                                                       onChange={(e) => {
                                                         setCFormData({
                                                           ...CFormData,
                                                           accommodation: formData.id,
                                                           [field.name]: e.target.value
                                                         })
                                                       }}
                                                       value={CFormData[field.name]}
                                            />
                                          )}
                                    </span>
                                      </div>
                                    </div>
                                  </React.Fragment>
                                )
                              }
                              if (field.type.includes("Enum")) {
                                return (
                                  <React.Fragment key={field.name || index}>
                                    <div className={"p-col-12 p-lg-6"}>
                                      <div className="p-field p-fluid">
                                        <label htmlFor={field.name}>{t(field.name)}</label>
                                        <span className="p-d-block">
                                      <Dropdown options={COptions[field.name]}
                                                filter
                                                optionLabel={(option) => window.App.getTranslatedLabel(option.value)}
                                                optionValue="value"
                                                id={field.name}
                                                emptyMessage={t('empty_message')}
                                                onChange={(e) => {
                                                  setCFormData({
                                                    ...CFormData,
                                                    [field.name]: e.value
                                                  })
                                                }}
                                                value={CFormData[field.name]}
                                      />
                                    </span>
                                      </div>
                                    </div>
                                  </React.Fragment>
                                )
                              }
                            })}
                          </>
                        }
                      </div>
                      <div className={"p-grid"}>
                        <div className={"p-col p-text-right p-col-align-center"}>
                          <Button type="button" label={t('save')} icon={"pi pi-save"}
                                  loading={loading}
                                  disabled={!formData.id}
                                  onClick={() => {
                                    setLoading(true)
                                    if (CFormData.id) {
                                      axios.patch(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST
                                        + "accommodation-" + Object.keys(formData._country_specifics)[0].toLowerCase() + "/"
                                        + formData['accommodation_' + Object.keys(formData._country_specifics)[0].toLowerCase() + "s"][0].id,
                                        CFormData,
                                        {
                                          params: {},
                                          headers: {
                                            'Authorization': 'Bearer ' + Cookies.get("userToken"),
                                            "Sys-name": Cookies.get("instanceId"),
                                            "Preferred-locale": Cookies.get("language")
                                          }
                                        })
                                        .then(response => {
                                          setCFormData(response.data.data);
                                          setLoading(false)
                                          window.App.toastShow('success', t('success'), response.data.message)
                                        })
                                        .catch(error => {
                                          setLoading(false)
                                          window.App.toastShow('error', t('error'), error.response.data.message, '', error.response)
                                        })
                                    } else {
                                      axios.post(process.env.REACT_APP_API_PROTOCOL + process.env.REACT_APP_API_HOST
                                        + "accommodation-" + Object.keys(formData._country_specifics)[0].toLowerCase(),
                                        CFormData,
                                        {
                                          params: {},
                                          headers: {
                                            'Authorization': 'Bearer ' + Cookies.get("userToken"),
                                            "Sys-name": Cookies.get("instanceId"),
                                            "Preferred-locale": Cookies.get("language")
                                          }
                                        })
                                        .then(response => {
                                          setCFormData(response.data.data);
                                          setLoading(false)
                                          window.App.toastShow('success', t('success'), response.data.message)
                                        })
                                        .catch(error => {
                                          setLoading(false)
                                          window.App.toastShow('error', t('error'), error.response.data.message, '', error.response)
                                        })
                                    }
                                  }}
                                  className="p-button-success p-ml-2"/>
                        </div>
                      </div>

                      {Object.keys(formData._country_specifics)[0].toLowerCase() === 'hu' &&
                        <>
                          <hr className={"p-my-4"}/>
                          <h3 className={"p-mt-0"}>{t('ntak_validation')}</h3>
                          <div className={"p-grid"}>
                            <div className={"p-col-12"}>
                              {isCsrReady &&
                                <HelpComponent
                                  type={"custom_message"}
                                  level={"LEVEL_SUCCESS"}
                                  message={t('hotelconnect_csr_ready_hint')}
                                />
                              }
                              {hasHotelConnectFailure &&
                                <HelpComponent
                                  type={"custom_message"}
                                  level={"LEVEL_DANGER"}
                                  message={t('hotelconnect_sync_failed_warning')}
                                />
                              }
                              <ul className="p-pl-0 p-mb-0 p-mt-3" style={{listStyle: "none"}}>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    {renderLabelWithErrorIcon(
                                      'registration',
                                      t('hotelconnect_registration_status_label'),
                                      hotelConnectRegistrationError
                                    )}
                                    <span
                                      className={classNames(
                                        'p-badge',
                                        getHotelConnectStatusMeta(hotelConnectRegistrationStatus).badgeClass,
                                        `tt-hotelconnect-retry-accommodation-form-${accommodationFormSyncIdentifier}`
                                      )}
                                      data-pr-tooltip={shouldShowHotelConnectSyncRetry ? t('hotelconnect_sync_retry_label') : undefined}
                                      role={shouldShowHotelConnectSyncRetry ? 'button' : undefined}
                                      tabIndex={shouldShowHotelConnectSyncRetry
                                        ? (hotelConnectSyncLoading || ntakActionLoading || !hasAccommodationHu ? -1 : 0)
                                        : undefined}
                                      style={{
                                        ...getHotelConnectStatusMeta(hotelConnectRegistrationStatus).style,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        fontWeight: 700,
                                        gap: '0.35rem',
                                        cursor: shouldShowHotelConnectSyncRetry
                                        && !hotelConnectSyncLoading
                                        && !ntakActionLoading
                                        && hasAccommodationHu ? 'pointer' : 'default'
                                      }}
                                      onClick={() => {
                                        if (!shouldShowHotelConnectSyncRetry || hotelConnectSyncLoading || ntakActionLoading || !hasAccommodationHu) {
                                          return;
                                        }
                                        handleHotelConnectSyncRetry();
                                      }}
                                      onKeyDown={(event) => {
                                        if (!shouldShowHotelConnectSyncRetry || hotelConnectSyncLoading || ntakActionLoading || !hasAccommodationHu) {
                                          return;
                                        }
                                        if (event.key === 'Enter' || event.key === ' ') {
                                          event.preventDefault();
                                          handleHotelConnectSyncRetry();
                                        }
                                      }}
                                    >
                                      <span>{getHotelConnectStatusMeta(hotelConnectRegistrationStatus).label}</span>
                                      {shouldShowHotelConnectSyncRetry &&
                                        <>
                                          <i
                                            className={`pi ${hotelConnectSyncLoading ? 'pi-spin pi-spinner' : 'pi-refresh'}`}
                                            style={{color: 'inherit', fontSize: '0.8rem'}}
                                          />
                                          <Tooltip
                                            target={`.tt-hotelconnect-retry-accommodation-form-${accommodationFormSyncIdentifier}`}
                                            position="left"
                                          />
                                        </>
                                      }
                                    </span>
                                  </div>
                                </li>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    {renderLabelWithErrorIcon(
                                      'csr',
                                      t('hotelconnect_csr_status_label'),
                                      hotelConnectCsrError
                                    )}
                                    <span
                                      className={classNames('p-badge', getHotelConnectStatusMeta(hotelConnectCsrStatus).badgeClass)}
                                      style={{
                                        ...getHotelConnectStatusMeta(hotelConnectCsrStatus).style,
                                        fontWeight: 700
                                      }}
                                    >
                                      {getHotelConnectStatusMeta(hotelConnectCsrStatus).label}
                                    </span>
                                  </div>
                                </li>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    {renderLabelWithErrorIcon(
                                      'certificate',
                                      t('hotelconnect_certificate_status_label'),
                                      hotelConnectCertificateError
                                    )}
                                    <span
                                      className={classNames('p-badge', getHotelConnectStatusMeta(hotelConnectCertificateStatus).badgeClass)}
                                      style={{
                                        ...getHotelConnectStatusMeta(hotelConnectCertificateStatus).style,
                                        fontWeight: 700
                                      }}
                                    >
                                      {getHotelConnectStatusMeta(hotelConnectCertificateStatus).label}
                                    </span>
                                  </div>
                                </li>
                                {hasHotelConnectCertificateUploadedAt &&
                                  <li className="p-mb-3">
                                    <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                         style={{gap: '0.5rem'}}>
                                      <span>{t('hotelconnect_certificate_uploaded_at_label')}</span>
                                      <span className="p-text-bold">{formatDateTimeValue(hotelConnectCertificateUploadedAt)}</span>
                                    </div>
                                  </li>
                                }
                                {hasHotelConnectAccommodationId &&
                                  <li>
                                    <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                         style={{gap: '0.5rem'}}>
                                      <span>{t('hotelconnect_accommodation_id_label')}</span>
                                      <span className="p-text-bold">{hotelConnectAccommodationId}</span>
                                    </div>
                                  </li>
                                }
                              </ul>
                            </div>
                          </div>
                          <div className={"p-grid p-m-0 p-p-0"}>
                            <div
                              className={classNames(
                                "p-col-12 p-my-0 p-py-0",
                                canGenerateHotelConnectTestCertificate ? "p-lg-4" : "p-lg-6"
                              )}
                            >
                              <div className="">
                                <span className="p-d-block">
                                  <div style={{position: 'relative'}}>
                                    <Button
                                      type="button"
                                      label={t('hotelconnect_csr_download_label')}
                                      icon={"pi pi-download"}
                                      loading={ntakActionLoading}
                                      className="p-button-success p-d-block w-100"
                                      style={shouldDisableNtakActions ? {opacity: 0.6} : undefined}
                                      disabled={ntakActionLoading || !isCsrStatusSuccess}
                                      onClick={handleCsrDownload}
                                    />
                                    {!ntakActionLoading && !isCsrStatusSuccess &&
                                      <button
                                        type="button"
                                        aria-label={t('hotelconnect_csr_download_label')}
                                        onClick={showHotelConnectCsrStatusWarning}
                                        className={"w-100"}
                                        style={{
                                          position: 'absolute',
                                          inset: 0,
                                          width: '100%',
                                          height: '100%',
                                          cursor: 'not-allowed',
                                          background: 'transparent',
                                          border: 0
                                        }}
                                      />
                                    }
                                  </div>
                                </span>
                              </div>
                            </div>
                            <div
                              className={classNames(
                                "p-col-12 p-my-0 p-py-0",
                                canGenerateHotelConnectTestCertificate ? "p-lg-4" : "p-lg-6"
                              )}
                            >
                              <div className="">
                                <span className="p-d-block">
                                  <div style={{position: 'relative'}}>
                                    <FileUpload mode="basic"
                                                auto
                                                chooseLabel={t('hotelconnect_certificate_upload_label')}
                                                name="file"
                                                chooseOptions={{className: 'w-100'}}
                                                disabled={shouldDisableNtakActions || ntakActionLoading || !isCsrStatusSuccess}
                                                onBeforeSelect={() => {
                                                  if (!isCsrStatusSuccess) {
                                                    showHotelConnectCsrStatusWarning();
                                                    return false;
                                                  }
                                                  if (shouldDisableNtakActions) {
                                                    showNtakValidationSettingsWarning();
                                                    return false;
                                                  }
                                                  return true;
                                                }}
                                                onSelect={(e) => {
                                                  if (e?.files?.[0]) {
                                                    handleCertificateUpload(e.files[0]);
                                                  }
                                                }}
                                    />
                                    {(shouldDisableNtakActions || !isCsrStatusSuccess) && !ntakActionLoading &&
                                      <button
                                        type="button"
                                        aria-label={t('hotelconnect_certificate_upload_label')}
                                        onClick={isCsrStatusSuccess ? showNtakValidationSettingsWarning : showHotelConnectCsrStatusWarning}
                                        className={"w-100"}
                                        style={{
                                          position: 'absolute',
                                          inset: 0,
                                          width: '100%',
                                          height: '100%',
                                          cursor: 'not-allowed',
                                          background: 'transparent',
                                          border: 0
                                        }}
                                      />
                                    }
                                  </div>
                                </span>
                              </div>
                            </div>
                            {canGenerateHotelConnectTestCertificate &&
                              <div className={"p-col-12 p-lg-4 p-my-0 p-py-0"}>
                                <div className="p-field p-fluid">
                                  <span className="p-d-block">
                                    <Button
                                      type="button"
                                      label={'Teszt tanúsítvány generálása'}
                                      icon={"pi pi-download"}
                                      loading={hotelConnectTestCertificateLoading}
                                      disabled={ntakActionLoading || hotelConnectTestCertificateLoading}
                                      onClick={handleHotelConnectTestCertificateDownload}
                                      className="p-button-help p-d-block w-100"
                                    />
                                  </span>
                                </div>
                              </div>
                            }
                          </div>
                          <hr className={"p-my-5"}/>
                          <h3 className={"p-mt-0 p-mb-0"}>{t('ntak_daily_closure_sync_title')}</h3>
                          <div className={"p-grid"}>
                            <div className={"p-col-12"}>
                              <ul className="p-pl-0 p-mb-0 p-mt-3" style={{listStyle: "none"}}>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    <span>{t('daily_closure_current_status_label')}</span>
                                    <span
                                      className={"p-badge"}
                                      style={{
                                        ...getDailyClosureStatusMeta(dailyClosureStatus?.status).style,
                                        width: '110px',
                                        minWidth: '110px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700
                                      }}
                                    >
                                      {getDailyClosureStatusMeta(dailyClosureStatus?.status).label}
                                    </span>
                                  </div>
                                </li>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    <span>{t('daily_closure_last_closed_day_label')}</span>
                                    <span className="p-text-bold">{dailyClosureStatus?.lastClosedDay || '-'}</span>
                                  </div>
                                </li>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    {renderLabelWithErrorIcon(
                                      'daily-closure-last-attempt',
                                      t('daily_closure_last_attempt_status_label'),
                                      dailyClosureStatus?.lastAttempt?.error
                                    )}
                                    <span
                                      className={"p-badge"}
                                      style={{
                                        ...getDailyClosureAttemptStatusMeta(dailyClosureStatus?.lastAttempt?.status).style,
                                        width: '110px',
                                        minWidth: '110px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700
                                      }}
                                    >
                                      {getDailyClosureAttemptStatusMeta(dailyClosureStatus?.lastAttempt?.status).label}
                                    </span>
                                  </div>
                                </li>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    <span>{t('daily_closure_last_attempt_happened_at_label')}</span>
                                    <span className="p-text-bold">{formatDailyClosureDateTimeValue(dailyClosureStatus?.lastAttempt?.happenedAt)}</span>
                                  </div>
                                </li>
                                <li className="p-mb-3">
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    <span>{t('daily_closure_next_run_at_label')}</span>
                                    <span className="p-text-bold">{formatDailyClosureDateTimeValue(dailyClosureStatus?.nextRunAt)}</span>
                                  </div>
                                </li>
                                <li>
                                  <div className="p-d-flex p-ai-center p-jc-between p-flex-wrap"
                                       style={{gap: '0.5rem'}}>
                                    <span>{t('daily_closure_next_sync_dates_label')}</span>
                                    <span className="p-text-bold">
                                      {(dailyClosureStatus?.willSyncDates || []).length > 0
                                        ? dailyClosureStatus.willSyncDates.join(', ')
                                        : '-'}
                                    </span>
                                  </div>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </>
                      }
                    </>
                  }
                </Panel>
              </TabPanel>
            </TabView>
          </form>
        )}/>
      </BlockUI>
      {locking === true &&
        <div className={"p-mt-2"}>
          <i className={"pi pi-exclamation-circle"}></i>
          &nbsp;
          {t('form_under_edit')}
          {formData && formData.under_edit_by_name_snap &&
            <>&nbsp;{t('form_under_edit_by')}: {formData.under_edit_by_name_snap}</>
          }
        </div>
      }
    </>
  )
}
export default withTranslation('common')(_FM_AccommodationFormComponent);
